/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select as ChakraSelect,
  SimpleGrid,
  Text,
  VStack,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAuthState } from '../../../../../auth/AuthProvider'
import { useAccount } from '../../../account/AccountProvider'
import { type Appointment } from '../../../../../model/appointment'
import { getDoctorsService } from '../../../../../services/Services'
import { normalizeAppointmentSettings } from '../../../../../utils/appointmentUtils'
import SearchableSelect from 'react-select'

const DAYS = [0, 1, 2, 3, 4, 5, 6]

const CLINIC_TIMEZONES = [
  'UTC',
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Addis_Ababa',
  'Africa/Algiers',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Khartoum',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Tunis',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Chicago',
  'America/Denver',
  'America/Halifax',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Sao_Paulo',
  'America/St_Johns',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Amman',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Beirut',
  'Asia/Damascus',
  'Asia/Dhaka',
  'Asia/Dubai',
  'Asia/Gaza',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kathmandu',
  'Asia/Kolkata',
  'Asia/Kuwait',
  'Asia/Manila',
  'Asia/Muscat',
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Honolulu'
] as const

const TIMEZONE_OPTIONS = CLINIC_TIMEZONES.map((timezone) => ({
  value: timezone,
  label: timezone.replaceAll('_', ' ')
}))

export default function AppointmentSettingsSection() {
  const { uid } = useAuthState()
  const account = useAccount()
  const { formatMessage } = useIntl()
  const toast = useToast()
  const [settings, setSettings] = useState(() =>
    normalizeAppointmentSettings(account.appointmentSettings)
  )
  const [saving, setSaving] = useState(false)
  const hasLocalEdits = useRef(false)

  useEffect(() => {
    if (!hasLocalEdits.current) {
      setSettings(normalizeAppointmentSettings(account.appointmentSettings))
    }
  }, [account.appointmentSettings])

  const editSettings = (
    update: (current: typeof settings) => typeof settings
  ) => {
    hasLocalEdits.current = true
    setSettings(update)
  }

  const save = async () => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: settings.timezone }).format()
    } catch {
      toast({
        title: formatMessage({ id: 'settings.appointments.invalidTimezone' }),
        status: 'error'
      })
      return
    }
    const invalidHours = DAYS.some((day) => {
      const hours = settings.weeklyHours[day]
      return hours.enabled && hours.start >= hours.end
    })
    if (invalidHours) {
      toast({
        title: formatMessage({ id: 'settings.appointments.invalidHours' }),
        status: 'error'
      })
      return
    }
    setSaving(true)
    try {
      await getDoctorsService().updateDoctorProfile({
        ownerId: uid,
        doctorData: { appointmentSettings: settings }
      })
      hasLocalEdits.current = false
      toast({
        title: formatMessage({ id: 'settings.appointments.saved' }),
        status: 'success'
      })
    } catch {
      toast({
        title: formatMessage({ id: 'settings.appointments.saveError' }),
        status: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box
      p={6}
      bg="white"
      borderRadius="10px"
      borderWidth="1px"
      borderColor="gray.200"
    >
      <Heading size="md">
        {formatMessage({ id: 'settings.appointments.title' })}
      </Heading>
      <Text mt={1} color="gray.500" fontSize="sm">
        {formatMessage({ id: 'settings.appointments.description' })}
      </Text>
      <Divider my={5} />
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={6}>
        <FormControl>
          <FormLabel htmlFor="clinic-timezone">
            {formatMessage({ id: 'settings.appointments.timezone' })}
          </FormLabel>
          <SearchableSelect
            inputId="clinic-timezone"
            aria-label={formatMessage({ id: 'settings.appointments.timezone' })}
            isSearchable
            isClearable={false}
            options={TIMEZONE_OPTIONS}
            value={
              TIMEZONE_OPTIONS.find(
                (option) => option.value === settings.timezone
              ) ?? null
            }
            placeholder={formatMessage({
              id: 'settings.appointments.timezonePlaceholder'
            })}
            noOptionsMessage={() =>
              formatMessage({ id: 'settings.appointments.noTimezones' })
            }
            onChange={(option) => {
              if (!option) return
              editSettings((current) => ({
                ...current,
                timezone: option.value
              }))
            }}
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '40px',
                borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
                borderRadius: '6px',
                boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
                ':hover': {
                  borderColor: state.isFocused ? '#3182ce' : '#cbd5e0'
                }
              }),
              menu: (base) => ({ ...base, zIndex: 20 }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#1a202c'
                  : state.isFocused
                    ? '#f7fafc'
                    : 'white',
                color: state.isSelected ? 'white' : '#2d3748',
                fontSize: '14px'
              })
            }}
          />
        </FormControl>
        <FormControl>
          <FormLabel>
            {formatMessage({ id: 'settings.appointments.defaultDuration' })}
          </FormLabel>
          <ChakraSelect
            value={settings.defaultDuration}
            onChange={(event) => {
              editSettings((current) => ({
                ...current,
                defaultDuration: Number(
                  event.target.value
                ) as Appointment['duration']
              }))
            }}
          >
            {[15, 30, 45, 60].map((duration) => (
              <option key={duration} value={duration}>
                {duration} {formatMessage({ id: 'calendar.minutes' })}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>
      </SimpleGrid>
      <VStack align="stretch" spacing={3}>
        {DAYS.map((day) => {
          const hours = settings.weeklyHours[day]
          return (
            <Flex
              key={day}
              gap={3}
              align="center"
              direction={{ base: 'column', sm: 'row' }}
            >
              <Checkbox
                minW="140px"
                isChecked={hours.enabled}
                onChange={(event) => {
                  editSettings((current) => ({
                    ...current,
                    weeklyHours: {
                      ...current.weeklyHours,
                      [day]: { ...hours, enabled: event.target.checked }
                    }
                  }))
                }}
              >
                {formatMessage({ id: `calendar.day.${day}` })}
              </Checkbox>
              <Input
                aria-label={`${formatMessage({ id: `calendar.day.${day}` })} ${formatMessage({ id: 'settings.appointments.opens' })}`}
                type="time"
                isDisabled={!hours.enabled}
                value={hours.start}
                onChange={(event) => {
                  editSettings((current) => ({
                    ...current,
                    weeklyHours: {
                      ...current.weeklyHours,
                      [day]: { ...hours, start: event.target.value }
                    }
                  }))
                }}
              />
              <Text color="gray.500">—</Text>
              <Input
                aria-label={`${formatMessage({ id: `calendar.day.${day}` })} ${formatMessage({ id: 'settings.appointments.closes' })}`}
                type="time"
                isDisabled={!hours.enabled}
                value={hours.end}
                onChange={(event) => {
                  editSettings((current) => ({
                    ...current,
                    weeklyHours: {
                      ...current.weeklyHours,
                      [day]: { ...hours, end: event.target.value }
                    }
                  }))
                }}
              />
            </Flex>
          )
        })}
      </VStack>
      <Button
        mt={6}
        colorScheme="blue"
        onClick={() => {
          void save()
        }}
        isLoading={saving}
      >
        {formatMessage({ id: 'settings.appointments.save' })}
      </Button>
    </Box>
  )
}
